import VantaBase from "./_base.js";
import { normalize, resolveTarget, presets, defaults } from "./options.js";
const effects = new Map(),
  instances = new Map();
const automatic = new Set();
export const version = "1.0.0-rc.1";
export { VantaBase, presets };
export function register(name, Effect) {
  name = String(name).toLowerCase();
  if (
    !/^[a-z][a-z0-9-]*$/.test(name) ||
    typeof Effect !== "function" ||
    !(Effect.prototype instanceof VantaBase)
  )
    throw new TypeError("Register a name and a VantaBase subclass");
  if (effects.has(name) && effects.get(name) !== Effect)
    throw new Error(`Effect already registered: ${name}`);
  effects.set(name, Effect);
  return (options) => create({ ...options, effect: name });
}
export const list = () => [...effects.keys()];
export function describe(name) {
  const Effect = effects.get(String(name).toLowerCase());
  if (!Effect) throw new Error(`Unknown effect: ${name}`);
  const d = {
    ...defaults,
    ...(Effect.prototype.getDefaultOptions?.() ||
      Effect.prototype.defaultOptions ||
      {}),
  };
  return {
    name,
    renderer: Effect.prototype.canvas2D ? "canvas2d" : "webgl2",
    options: Object.fromEntries(
      Object.entries(d).map(([key, value]) => [
        key,
        {
          type: value === null ? "integer|null" : typeof value,
          default: value,
        },
      ]),
    ),
  };
}
export function create(input) {
  if (!input || typeof input !== "object")
    throw new TypeError("create requires {effect,target}");
  const name = String(input.effect || "").toLowerCase(),
    Effect = effects.get(name);
  if (!Effect) throw new Error(`Unknown effect: ${name}. Use VantaNext.list()`);
  const el = resolveTarget(input.target || input.el);
  const options = normalize(
    { ...input, effect: name, target: el },
    Effect.prototype.getDefaultOptions?.() ||
      Effect.prototype.defaultOptions ||
      {},
  );
  const previous = instances.get(el);
  if (
    previous?.effect === name &&
    previous.status !== "destroyed" &&
    previous.status !== "fallback"
  ) {
    const { target, el: unused, effect, ...patch } = input;
    previous.update(patch);
    return previous;
  }
  previous?.destroy();
  const instance = new Effect(options);
  if (instance.status !== "destroyed") instances.set(el, instance);
  instance.onDisposed = () => {
    if (instances.get(el) === instance) instances.delete(el);
  };
  instance.boot();
  return instance;
}
export const get = (target) => instances.get(resolveTarget(target));
export function createAll(targets, options = {}) {
  const nodes =
    typeof targets === "string" ? document.querySelectorAll(targets) : targets;
  return Array.from(nodes, (el) => create({ ...options, target: el }));
}
export function destroy(target) {
  get(target)?.destroy();
}
export function destroyAll() {
  for (const i of instances.values()) i.destroy();
}
export function applyPreset(target, quality) {
  const i = target instanceof VantaBase ? target : get(target);
  if (!i) throw new Error("No instance");
  return i.update({ quality });
}
export function auto({ root = document, observe = true, options = {} } = {}) {
  let stopped = false;
  const visit = (el) => {
    if (!el.matches?.("[data-vanta-effect]")) return;
    try {
      const config = JSON.parse(el.getAttribute("data-vanta-options") || "{}");
      create({
        ...options,
        ...config,
        effect: el.getAttribute("data-vanta-effect"),
        target: el,
      });
    } catch (error) {
      el.dispatchEvent(new CustomEvent("vanta:error", { detail: { error } }));
    }
  };
  const scan = (node) => {
    visit(node);
    node.querySelectorAll?.("[data-vanta-effect]").forEach(visit);
  };
  scan(root);
  const observer = observe
    ? new MutationObserver((records) => {
        if (stopped) return;
        for (const record of records) {
          if (record.type === "attributes") {
            if (record.target.hasAttribute("data-vanta-effect"))
              visit(record.target);
            else instances.get(record.target)?.destroy();
          } else
            for (const node of record.addedNodes)
              if (node.nodeType === 1 && !node.hasAttribute("data-vanta-layer"))
                scan(node);
        }
      })
    : null;
  observer?.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["data-vanta-effect", "data-vanta-options"],
  });
  const controller = {
    disconnect() {
      stopped = true;
      observer?.disconnect();
      automatic.delete(controller);
    },
  };
  automatic.add(controller);
  return controller;
}
export function stopAuto() {
  for (const controller of automatic) controller.disconnect();
}
